/**
 * Reload once when a deploy has replaced the code files an open tab still asks for.
 *
 * Every deploy renames the code/style files. A tab opened before it still runs the old code, and
 * the first page it opens lazily asks for an old file that is gone — the server answers with the
 * home page instead, the browser throws ChunkLoadError, and the page showed "Something went wrong"
 * (concierge-fast-api/logs/app.log.3, 2026-10-03). One reload fetches the new version.
 *
 * Guarded: never more than one automatic reload per RELOAD_GUARD_MS, so a truly broken deploy
 * shows the normal error screen instead of reloading forever. No usable storage → no automatic
 * reload at all (the guard cannot be kept).
 */
export const RELOAD_KEY = 'concierge:chunk-reload-at';
export const RELOAD_GUARD_MS = 60 * 1000;

export const isChunkLoadError = (error) => {
  if (!error) return false;
  if (error.name === 'ChunkLoadError') return true;
  return /Loading (CSS )?chunk [\w-]+ failed/i.test(String(error.message || ''));
};

const defaultStorage = () => {
  try { return window.sessionStorage; } catch { return null; }
};

export const reloadOnceForNewVersion = (error, {
  storage = defaultStorage(), reload = () => window.location.reload(), now = Date.now(),
} = {}) => {
  if (!isChunkLoadError(error) || !storage) return false;
  try {
    const last = Number(storage.getItem(RELOAD_KEY) || 0);
    if (last && now - last < RELOAD_GUARD_MS) return false;   // just reloaded: do not loop
    storage.setItem(RELOAD_KEY, String(now));
  } catch {
    return false;
  }
  reload();
  return true;
};
